import React, {FC, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {Formik, Field, Form, FieldInputProps, FormikProps} from 'formik';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ConfirmationModal from '../shared/ConfirmationModal';
import NewconfirmationModal from '../shared/NewconfirmationModal';
import AllFailureModal from '../shared/AllFailureModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Skill {
    skill_id: string;
    skill_name: string;
    skill_shortname: string;
    skill_tag: string;
    skill_category: string;
}

interface SkillsAutocompleteProps {
    value: string | Skill[];
    onChange: (skills: Skill[]) => void;
    onBlur?:  (e: FocusEvent) => void;
    skillsList: Skill[];
}

const SkillsAutocomplete: React.FC<SkillsAutocompleteProps> = ({ value, onChange, onBlur, skillsList }) => {
    const initialValue = Array.isArray(value) ? value : value ? value.split(",").map((v) => ({ skill_id: v, skill_name: v, skill_shortname: "", skill_tag: "", skill_category: "" })) : [];

    const [inputValue, setInputValue] = useState<string>('');
    const [selectedSkills, setSelectedSkills] = useState<Skill[]>(initialValue);
    const [suggestions, setSuggestions] = useState<Skill[]>([]);
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const suggestionsRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const skillsArray = Array.isArray(value) ? value : value?.split(",");

        const mappedSkills = skillsArray.map(skill => {
            if (typeof skill === 'object' && skill.skill_id) {
                return skill;
            }
            const foundSkill = skillsList.find(s => s.skill_id === skill);
            return foundSkill || null;
        }).filter(Boolean);

        // @ts-ignore
        setSelectedSkills(mappedSkills);
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);

        if (value.trim()) {
            const filtered = skillsList.filter(skill => {
                const searchTerm = value.toLowerCase();
                const alreadySelected = selectedSkills.some(selected => selected.skill_id === skill.skill_id);

                return !alreadySelected && (
                    skill.skill_name.toLowerCase().includes(searchTerm) ||
                    skill.skill_shortname.toLowerCase().includes(searchTerm) ||
                    skill.skill_tag.toLowerCase().includes(searchTerm)
                );
            });
            setSuggestions(filtered);
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const addSkill = (skill: Skill) => {
        if (!selectedSkills.some(selected => selected.skill_id === skill.skill_id)) {
            const newSkills = [...selectedSkills, skill];
            setSelectedSkills(newSkills);
            onChange(newSkills);
            setInputValue('');
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const removeSkill = (skillToRemove: Skill) => {
        const newSkills = selectedSkills.filter(skill => skill.skill_id !== skillToRemove.skill_id);
        setSelectedSkills(newSkills);
        onChange(newSkills);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && inputValue.trim() !== '') {
            e.preventDefault();
            if (suggestions.length > 0) {
                addSkill(suggestions[0]);
            }
        }
    };

    const renderSkillBadge = (skill: Skill) => {
        const tags = skill?.skill_tag?.split(',').slice(0, 2).map(tag => tag.trim());
        return (
            <span
                key={skill.skill_id}
                className="badge bg-primary me-2 mb-2 d-inline-flex align-items-center"
            >
        <span className="d-flex flex-column">
          <span>{skill.skill_name}</span>
          <small className="text-white-50">
            {tags?.join(' • ')}
          </small>
        </span>
        <button
            type="button"
            className="btn-close btn-close-white ms-2"
            style={{ fontSize: '0.5rem' }}
            onClick={() => removeSkill(skill)}
            aria-label="Remove skill"
        />
      </span>
        );
    };

    const renderSuggestionItem = (skill: Skill) => {
        return (
            <div
                key={skill.skill_id}
                className="p-2 cursor-pointer table-hover border rounded"
                style={{ cursor: 'pointer' }}
                onMouseDown={() => addSkill(skill)}
            >
                <div className="d-flex flex-column">
                    <span className="fw-medium">{skill.skill_name}</span>
                    <small className="text-muted">
                        {skill.skill_category} • {skill.skill_shortname}
                    </small>
                    <small className="text-muted">
                        Tags: {skill.skill_tag}
                    </small>
                </div>
            </div>
        );
    };

    return (
        <div className="skills-autocomplete">
            <div className="mb-2">
                {selectedSkills.map(renderSkillBadge)}
            </div>
            <div className="position-relative">
                <input
                    type="text"
                    ref={inputRef}
                    className="form-control"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={() => {
                        setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    placeholder="Search skills by name, shortname, or tags..."
                />
                {showSuggestions && suggestions.length > 0 && (
                    <div
                        ref={suggestionsRef}
                        className="position-absolute w-100 mt-1 shadow bg-white rounded border"
                        style={{
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto',
                        }}
                    >
                        {suggestions.map(renderSuggestionItem)}
                    </div>
                )}
            </div>
        </div>
    );
};

const CandidateUpdate: FC = () => {
    const { candidateId } = useParams();
    const [skills, setSkills] = useState<Skill[]>([]);
    const [updateData, setUpdateData] = useState({
        userid: candidateId,
        city: '',
        hometown: '',
        college: '',
        university: '',
        passing_year: '',
        branch: '',
      // isactive: '',
        whatsapp_mobile: '',
        skills: ""
    });
    const [showModal, setShowModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showFailureModal, setShowFailureModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [failureMessage, setFailureMessage] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [confirmationCallback, setConfirmationCallback] = useState<(() => void) | null>(null);
    const navigate = useNavigate();
  
    const callCandidateUpdateApi = async (data: any) => {
      try {
        const url = process.env.REACT_APP_API_URL + 'candidate_details/updatecandidate';
        const response = await axios.put(url, data);
        setSuccessMessage('Candidate updated Successfully');
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate('/candidate_details');
        }, 3000);
      } catch (error) {
        setFailureMessage('Error updating candidate');
        setShowFailureModal(true);
      }
    };
  
    const getCandidateByIdApi = async (candidateId: any) => {
      try {
        const url = process.env.REACT_APP_API_URL + 'candidate_details/showcandidatebyid/' + candidateId;
        const response = await axios.get(url);
        setUpdateData(response.data[0]);
        console.log(response.data[0].skills);
        console.log(typeof response.data[0].skills);
        return response.data[0];
      } catch (error) {
        console.error('Error fetching candidate by ID:', error);
        return {};
      }
    };
  
  useEffect(() => {
      const fetchAllSkills = async () => {
          try {
              const url = process.env.REACT_APP_API_URL + "skills/showallskills";
              const response = await axios.get(url);
              setSkills(response.data);
          } catch (error) {
              console.log(error);
              setFailureMessage('Error fetching skills');
              setShowFailureModal(true);
          }
      }
      fetchAllSkills();
      getCandidateByIdApi(candidateId);
    }, [candidateId]);

    const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      if (JSON.stringify(values) === JSON.stringify(updateData)) {
        setSuccessMessage('No Updates made');
          setShowSuccessModal(true);
        } else {
            setModalMessage('Are you sure you want to update candidate data?');
            setConfirmationCallback(() => () => {
              callCandidateUpdateApi(values);
              resetForm();
            });
            setShowModal(true);
          }
        } catch (error) {
          setFailureMessage('Error in form submission');
          setShowFailureModal(true);
        }
  };

  return (
    <>
    <ToastContainer/>
    <div>
      <h4 className='bg-info bg-opacity-25 text-center py-2 mb-3'>Update Candidate Details</h4>
        <Formik
            enableReinitialize={true}
            initialValues={updateData}
            onSubmit={handleSubmit}
        >
            <div className="container mt-4">
                <Form className="p-4 border rounded bg-light">
                    {/* Hidden User ID Field */}
                    <Field hidden type="number" name="userid" />

                    {/* City Field */}
                    <div className="mb-3 row">
                        <label htmlFor="city" className="col-md-4 col-form-label">
                            City:
                        </label>
                        <div className="col-md-8">
                            <Field name="city" type="text" className="form-control" />
                        </div>
                    </div>

                    {/* Hometown Field */}
                    <div className="mb-3 row">
                        <label htmlFor="hometown" className="col-md-4 col-form-label">
                            Hometown:
                        </label>
                        <div className="col-md-8">
                            <Field name="hometown" type="text" className="form-control" />
                        </div>
                    </div>

                    {/* College Field */}
                    <div className="mb-3 row">
                        <label htmlFor="college" className="col-md-4 col-form-label">
                            College:
                        </label>
                        <div className="col-md-8">
                            <Field name="college" type="text" className="form-control" />
                        </div>
                    </div>

                    {/* University Field */}
                    <div className="mb-3 row">
                        <label htmlFor="university" className="col-md-4 col-form-label">
                            University:
                        </label>
                        <div className="col-md-8">
                            <Field name="university" type="text" className="form-control" />
                        </div>
                    </div>

                    {/* Skills Autocomplete */}
                    <div className="mb-3 row">
                        <label htmlFor="skills" className="col-md-4 col-form-label">
                            Skills:
                        </label>
                        <div className="col-md-8">
                            <Field name="skills">
                                {({ field, form }: { field: FieldInputProps<any>; form: FormikProps<any> }) => (
                                    <SkillsAutocomplete
                                        value={updateData.skills || []}
                                        onChange={(skills: Skill[]) => form.setFieldValue('skills', skills)}
                                        onBlur={field.onBlur}
                                        skillsList={skills}
                                    />
                                )}
                            </Field>
                        </div>
                    </div>

                    {/* Passing Year Field */}
                    <div className="mb-3 row">
                        <label htmlFor="passing_year" className="col-md-4 col-form-label">
                            Passing Year:
                        </label>
                        <div className="col-md-8">
                            <Field name="passing_year" type="text" className="form-control" />
                        </div>
                    </div>

                    {/* Branch Field */}
                    <div className="mb-3 row">
                        <label htmlFor="branch" className="col-md-4 col-form-label">
                            Branch:
                        </label>
                        <div className="col-md-8">
                            <Field name="branch" type="text" className="form-control" />
                        </div>
                    </div>

                    {/* Whatsapp Number Field */}
                    <div className="mb-3 row">
                        <label htmlFor="whatsapp_mobile" className="col-md-4 col-form-label">
                            Whatsapp Number:
                        </label>
                        <div className="col-md-8">
                            <Field name="whatsapp_mobile" type="text" className="form-control" />
                        </div>
                    </div>
                    
                    
                    
                    
                    <div className="text-center">
                        <button type="submit" className="btn btn-primary">
                            Update
                        </button>
                        <Link to="/candidate_details" type="button" className="btn btn-danger me-3" >
                            Go Back
                        </Link>
                       
                    </div>
                    
                </Form>
            </div>
        </Formik>
        {/* Confirmation Modal */}
     <ConfirmationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        modalMessage={modalMessage}
        confirmation={confirmationCallback}
        operationType="Confirm"
      />
      <NewconfirmationModal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} modalMessage={successMessage} operationType="OK" timeoutDuration={3000} />
            <AllFailureModal show={showFailureModal} onHide={() => setShowFailureModal(false)} modalMessage={failureMessage}  operationType="OK" timeoutDuration={3000}/>
    </div>
    </>
  );
};

export default CandidateUpdate;